import { useEffect, useState } from "react";
import { Radio, CircleDot, MessageSquare, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PENDING_KEY = "pandax_creator_tour_pending";
const seenKey = (uid) => `pandax_creator_tour_seen_${uid}`;

export const markCreatorTourPending = () => sessionStorage.setItem(PENDING_KEY, "1");
export const replayCreatorTour = (uid) => { localStorage.removeItem(seenKey(uid)); markCreatorTourPending(); };

const STEPS = [
  { k: "golive", Icon: Radio, title: "Go Live in one tap", cta: "Open Go Live",
    body: "Hit Go Live from the left rail. Add a title, pick a filter, set a paid entry or a coin goal — then start. Viewers can gift you in real time." },
  { k: "wheel", Icon: CircleDot, title: "Spin up a Prize Wheel", cta: "See my Prizes tab",
    body: "Inside Go Live, tap Wheel to add prizes like a song request or a shout-out. Viewers pay coins to spin; every win lands in your Prizes tab for you to fulfil." },
  { k: "inbox", Icon: MessageSquare, title: "Run your Inbox", cta: "Open my Inbox",
    body: "Fan messages and gift thank-yous live here. Set an auto-reply for when you're offline and save reply templates so you never miss a fan." },
];

export default function CreatorWelcomeTour({ userId, onGoLive, onTab }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!userId) return;
    const check = () => {
      if (sessionStorage.getItem(PENDING_KEY) === "1" && !localStorage.getItem(seenKey(userId))) {
        setOpen(true);
        setI(0);
      }
    };
    check();
    window.addEventListener("creator-tour:replay", check);
    return () => window.removeEventListener("creator-tour:replay", check);
  }, [userId]);

  const finish = () => {
    localStorage.setItem(seenKey(userId), "1");
    sessionStorage.removeItem(PENDING_KEY);
    setOpen(false);
  };
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const act = () => {
    if (step.k === "golive") onGoLive?.();
    if (step.k === "wheel") onTab?.("prizes");
    if (step.k === "inbox") onTab?.("inbox");
    finish();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent data-testid="creator-tour" className="bg-zinc-950 border-white/10 text-white rounded-none max-w-md">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-rose-500" data-testid="creator-tour-step">Welcome, creator · step {i + 1} of {STEPS.length}</div>
          <div className="flex items-center gap-3 mt-2">
            <span className="w-11 h-11 grid place-items-center bg-rose-600/15 border border-rose-500/40 text-rose-300"><step.Icon size={20} /></span>
            <DialogTitle className="font-display text-2xl" data-testid="creator-tour-title">{step.title}</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-zinc-400 pt-2">{step.body}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 my-1">
          {STEPS.map((s, n) => <span key={s.k} className={`h-1 flex-1 transition-colors ${n <= i ? "bg-rose-600" : "bg-white/10"}`} />)}
        </div>
        <div className="flex items-center justify-between gap-3 pt-2">
          <button type="button" onClick={finish} data-testid="creator-tour-skip" className="text-xs text-zinc-500 hover:text-white">Skip tour</button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={act} data-testid="creator-tour-cta" className="rounded-none border-white/15 text-zinc-200 hover:text-white">{step.cta}</Button>
            {!last ? (
              <Button onClick={() => setI(i + 1)} data-testid="creator-tour-next" className="rounded-none bg-rose-600 hover:bg-rose-500 text-white">Next <ChevronRight size={14} className="ml-1" /></Button>
            ) : (
              <Button onClick={finish} data-testid="creator-tour-done" className="rounded-none bg-rose-600 hover:bg-rose-500 text-white">Done</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
